/**
 * Page Réglages → Grille CCQ Peintres (2026-05-06)
 *
 * Permet au propriétaire de :
 *  - Visualiser la grille en vigueur (4 grades × 16 colonnes + camions/outils)
 *  - Publier une nouvelle version (date d'effet) lors d'une mise à jour ACQ
 *  - Consulter l'historique des versions précédentes
 */
import axios from 'axios';
import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Save, X, History, Plus, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Ordre + libellés des colonnes (alignés sur la grille ACQ)
const COLUMNS = [
  { key: 'taux_salaire', label: 'Taux de salaire', shortLabel: 'Taux', unit: '$/h', employee: true },
  { key: 'vacances', label: 'Vacances 13 %', shortLabel: 'Vac.', unit: '$/h', employee: true },
  { key: 'salaire_brut', label: 'Salaire brut', shortLabel: 'Brut', unit: '$/h', employee: true, computed: true },
  { key: 'assurance_emploi', label: 'Assurance-emploi', shortLabel: 'A-E', unit: '$/h' },
  { key: 'rqap', label: 'RQAP', shortLabel: 'RQAP', unit: '$/h' },
  { key: 'rrq', label: 'RRQ', shortLabel: 'RRQ', unit: '$/h' },
  { key: 'fss', label: 'FSS', shortLabel: 'FSS', unit: '$/h' },
  { key: 'avantages_sociaux', label: 'Avantages sociaux', shortLabel: 'Av. soc.', unit: '$/h' },
  { key: 'taxe_assurance', label: 'Taxe assurance', shortLabel: 'Taxe ass.', unit: '$/h' },
  { key: 'cotisation_ccq', label: 'Cotisation CCQ', shortLabel: 'Cot. CCQ', unit: '$/h' },
  { key: 'cotisation_aecq_acq', label: 'Cotisation AECQ + ACQ', shortLabel: 'AECQ+ACQ', unit: '$/h' },
  { key: 'fonds_divers', label: 'Fonds divers', shortLabel: 'Fonds', unit: '$/h' },
  { key: 'equipement_securite', label: 'Équipement de sécurité', shortLabel: 'Équip. séc.', unit: '$/h', employee: true },
  { key: 'autres_contributions', label: 'Autres contributions', shortLabel: 'Autres', unit: '$/h' },
  { key: 'clauses_monetaires_normatives', label: 'Clauses monétaires normatives', shortLabel: 'Clauses mon.', unit: '$/h' },
  { key: 'cnesst', label: 'CNESST', shortLabel: 'CNESST', unit: '$/h' },
  { key: 'total_cout_main_oeuvre', label: 'Total — Coût main d\'œuvre', shortLabel: 'TOTAL M.O.', unit: '$/h', highlight: true, computed: true },
  { key: 'camions', label: 'Camions', shortLabel: 'Camions', unit: '$/h', optional: true },
  { key: 'outils', label: 'Outils', shortLabel: 'Outils', unit: '$/h', optional: true },
  { key: 'total_avant_admin', label: 'Total avant admin et profit', shortLabel: 'GRAND TOTAL', unit: '$/h', highlight: true, computed: true },
];

const GRADE_LABELS = {
  compagnon: 'Compagnon',
  apprenti_3: 'Apprenti P3',
  apprenti_2: 'Apprenti P2',
  apprenti_1: 'Apprenti P1',
};
const GRADE_ORDER = ['compagnon', 'apprenti_3', 'apprenti_2', 'apprenti_1'];

const fmt = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  return new Intl.NumberFormat('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 3 }).format(n);
};

export default function ConfigurationCCQ() {
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPublish, setShowPublish] = useState(false);
  const [draft, setDraft] = useState(null);
  const [draftDate, setDraftDate] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [draftSource, setDraftSource] = useState('ACQ');
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [aRes, hRes] = await Promise.all([
        axios.get(`${API_URL}/api/ccq/grille/active`),
        axios.get(`${API_URL}/api/ccq/grilles`),
      ]);
      setActive(aRes.data);
      setHistory(hRes.data || []);
    } catch (e) {
      toast.error('Erreur chargement grille CCQ');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  const openPublish = () => {
    if (!active) return;
    // On copie la grille active comme point de départ → l'utilisateur édite les valeurs qui changent
    setDraft(JSON.parse(JSON.stringify(active.grades)));
    setDraftDate(new Date().toISOString().slice(0, 10));
    setDraftNotes('');
    setDraftSource(active.source || 'ACQ');
    setShowPublish(true);
  };

  const updateDraftValue = (grade, key, value) => {
    const v = value === '' ? 0 : parseFloat(value);
    setDraft(prev => ({
      ...prev,
      [grade]: { ...prev[grade], [key]: isNaN(v) ? 0 : v },
    }));
  };

  const recomputeTotalsForGrade = (g) => {
    // Salaire brut = taux + vacances
    const salaire_brut = (g.taux_salaire || 0) + (g.vacances || 0);
    // Total main d'œuvre = salaire brut + colonnes 4-16
    const sumKeys = ['assurance_emploi', 'rqap', 'rrq', 'fss', 'avantages_sociaux', 'taxe_assurance',
      'cotisation_ccq', 'cotisation_aecq_acq', 'fonds_divers', 'equipement_securite',
      'autres_contributions', 'clauses_monetaires_normatives', 'cnesst'];
    const total_cout_main_oeuvre = salaire_brut + sumKeys.reduce((s, k) => s + (g[k] || 0), 0);
    const total_avant_admin = total_cout_main_oeuvre + (g.camions || 0) + (g.outils || 0);
    return { ...g, salaire_brut: round2(salaire_brut), total_cout_main_oeuvre: round2(total_cout_main_oeuvre), total_avant_admin: round2(total_avant_admin) };
  };

  const round2 = (n) => Math.round(n * 100) / 100;

  const draftRecomputed = useMemo(() => {
    if (!draft) return null;
    const out = {};
    for (const g of GRADE_ORDER) {
      out[g] = recomputeTotalsForGrade(draft[g] || {});
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const publish = async () => {
    if (!draftDate) { toast.error('Date d\'effet requise'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/ccq/grille`, {
        secteur: 'commercial_institutionnel_industriel',
        metier: 'peintre',
        source: draftSource,
        date_effet: draftDate,
        notes: draftNotes || null,
        grades: draftRecomputed,
      });
      toast.success(`Grille publiée — effet ${draftDate}`);
      setShowPublish(false);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur publication');
    } finally {
      setSaving(false);
    }
  };

  const deleteVersion = async (id) => {
    if (!window.confirm('Supprimer cette version de grille ? Les punchs déjà rattachés bloqueront la suppression.')) return;
    try {
      await axios.delete(`${API_URL}/api/ccq/grille/${id}`);
      toast.success('Version supprimée');
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    }
  };

  if (loading) return <div className="p-6 text-slate-500">Chargement…</div>;

  return (
    <div className="p-6 max-w-[1500px] mx-auto space-y-6" data-testid="page-ccq">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-violet-600" />
            Grille CCQ — Peintres
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Coûts horaires de la main-d'œuvre — secteur Commercial / Institutionnel / Industriel.
            Source de vérité pour le calcul des heures sur les BT marqués <Badge className="ml-1 bg-violet-100 text-violet-800 hover:bg-violet-100">CCQ</Badge>.
          </p>
        </div>
        <Button onClick={openPublish} className="bg-violet-600 hover:bg-violet-700" data-testid="btn-publish-ccq">
          <Plus className="w-4 h-4 mr-1.5" /> Publier nouvelle version
        </Button>
      </div>

      <Tabs defaultValue="active" className="space-y-4">
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active">Grille active</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="w-3.5 h-3.5 mr-1" /> Historique ({history.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          {active && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      Effet au {active.date_effet}
                      <Badge variant="outline" className="ml-2">{active.source}</Badge>
                    </CardTitle>
                    {active.notes && <CardDescription className="mt-1">{active.notes}</CardDescription>}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <GrilleTable grades={active.grades} readOnly />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history">
          <div className="space-y-3">
            {history.map(g => (
              <Card key={g.id} className={g.id === active?.id ? 'border-violet-300 bg-violet-50/30' : ''}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base">
                      Effet au {g.date_effet}
                      {g.id === active?.id && <Badge className="ml-2 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">EN VIGUEUR</Badge>}
                      <Badge variant="outline" className="ml-2 text-[10px]">{g.source}</Badge>
                    </CardTitle>
                    {g.notes && <CardDescription className="text-xs mt-0.5">{g.notes}</CardDescription>}
                  </div>
                  {g.id !== active?.id && (
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteVersion(g.id)} data-testid={`btn-delete-grille-${g.id}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="overflow-x-auto pt-0">
                  <GrilleTable grades={g.grades} readOnly compact />
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Modal publication d'une nouvelle version ── */}
      <Dialog open={showPublish} onOpenChange={(o) => !saving && setShowPublish(o)}>
        <DialogContent className="max-w-[95vw] sm:max-w-[1200px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Publier une nouvelle version de la grille CCQ</DialogTitle>
            <DialogDescription>
              Ajustez les valeurs qui ont changé. Les colonnes <strong>Salaire brut</strong>, <strong>Total M.O.</strong> et <strong>Grand Total</strong> sont recalculées automatiquement.
              Les anciennes versions sont conservées pour la traçabilité historique des punchs.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div>
              <Label>Date d'effet *</Label>
              <Input type="date" value={draftDate} onChange={e => setDraftDate(e.target.value)} data-testid="input-date-effet" />
            </div>
            <div>
              <Label>Source</Label>
              <Input value={draftSource} onChange={e => setDraftSource(e.target.value)} placeholder="ACQ / APCHQ / autre" data-testid="input-source" />
            </div>
            <div>
              <Label>Notes (optionnel)</Label>
              <Input value={draftNotes} onChange={e => setDraftNotes(e.target.value)} placeholder="Ex : Mise à jour annuelle CCQ" data-testid="input-notes" />
            </div>
          </div>

          {draftRecomputed && (
            <div className="overflow-x-auto">
              <GrilleTable grades={draftRecomputed} readOnly={false} onChange={updateDraftValue} />
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setShowPublish(false)} disabled={saving}>Annuler</Button>
            <Button onClick={publish} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="btn-confirm-publish">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Publication…' : 'Publier la nouvelle version'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function GrilleTable({ grades, readOnly, compact, onChange }) {
  return (
    <table className="w-full text-xs border-collapse" data-testid="table-grille-ccq">
      <thead>
        <tr className="bg-slate-100">
          <th className="text-left px-2 py-2 sticky left-0 bg-slate-100 z-10 border-b border-r whitespace-nowrap">Colonne</th>
          {GRADE_ORDER.map(g => (
            <th key={g} className="text-right px-2 py-2 border-b whitespace-nowrap">{GRADE_LABELS[g]}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {COLUMNS.map(col => {
          const isHighlight = col.highlight;
          const isComputed = col.computed;
          const showRow = !compact || isHighlight || ['taux_salaire', 'salaire_brut'].includes(col.key);
          if (!showRow) return null;
          return (
            <tr key={col.key} className={`${isHighlight ? 'bg-violet-50 font-bold border-y-2 border-violet-300' : 'hover:bg-slate-50'} ${col.optional ? 'text-slate-500' : ''}`}>
              <td className="px-2 py-1.5 sticky left-0 bg-inherit border-r whitespace-nowrap">
                <span className="font-medium">{col.label}</span>
                {col.employee && <Badge variant="outline" className="ml-1.5 text-[9px] px-1 py-0 border-emerald-300 text-emerald-700">EMPLOYÉ</Badge>}
                {col.optional && <Badge variant="outline" className="ml-1.5 text-[9px] px-1 py-0">OPT.</Badge>}
              </td>
              {GRADE_ORDER.map(g => {
                const v = grades?.[g]?.[col.key];
                if (readOnly || isComputed) {
                  return (
                    <td key={g} className="text-right px-2 py-1.5 tabular-nums">
                      {col.unit === '$/h' ? `${fmt(v)} $` : fmt(v)}
                    </td>
                  );
                }
                return (
                  <td key={g} className="px-1 py-1">
                    <Input
                      type="number"
                      step="0.001"
                      value={v ?? ''}
                      onChange={e => onChange(g, col.key, e.target.value)}
                      className="h-7 text-xs text-right tabular-nums"
                      data-testid={`input-${g}-${col.key}`}
                    />
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
